import { useMemo, useState } from 'react';
import { fmtDag, urenTussen } from '@ep/core';
import { api } from '../lib/api';
import { AFW_SOORT, useActie, useData } from '../lib/data';
import { AdresVeld, type GeoWaarde } from '../ui/AdresVeld';
import { Badge, Laden, TIJDEN, Veld, VerwijderKnop } from '../ui/ui';

export function Verlof() {
  const { data: d } = useData();
  const [f, setF] = useState({ engineerId: '', soort: 'VERLOF', van: '', tot: '', heleDag: true, vanTijd: '09:00', totTijd: '13:00', opmerking: '' });
  const [geo, setGeo] = useState<GeoWaarde>({ adres: '', lat: null, lon: null, naam: null, gewijzigd: false });
  const [toonOud, setToonOud] = useState(false);
  const [filterEng, setFilterEng] = useState('');
  const opslaan = useActie(
    () =>
      api.post('/api/afwezigheden', {
        engineerId: f.engineerId,
        soort: f.soort,
        van: f.van,
        tot: f.tot || f.van,
        vanTijd: f.heleDag ? null : f.vanTijd,
        totTijd: f.heleDag ? null : f.totTijd,
        opmerking: f.opmerking || null,
        adres: geo.adres || null,
        lat: geo.lat,
        lon: geo.lon,
      }),
    {
      succes: 'Afwezigheid opgeslagen; de agenda van de engineer wordt bijgewerkt',
      opNa: () => {
        setF({ ...f, van: '', tot: '', opmerking: '' });
        setGeo({ adres: '', lat: null, lon: null, naam: null, gewijzigd: false });
      },
    },
  );
  const verleng = useActie((id: string) => api.post(`/api/afwezigheden/${id}/verleng`), { succes: 'Met een dag verlengd' });
  const verwijder = useActie((id: string) => api.del(`/api/afwezigheden/${id}`), { succes: 'Afwezigheid verwijderd' });
  const lijst = useMemo(
    () =>
      (d?.afwezigheden ?? [])
        .filter((a) => toonOud || a.tot >= d!.vandaag)
        .filter((a) => !filterEng || a.engineerId === filterEng)
        .sort((a, b) => a.van.localeCompare(b.van)),
    [d, toonOud, filterEng],
  );
  if (!d) return <Laden />;
  const eng = (id: string) => d.engineers.find((e) => e.id === id)?.naam ?? '?';
  return (
    <div className="pagina" style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', gap: 16, alignItems: 'start' }}>
      <div className="paneel kolom">
        <h1>Verlof en afwezigheid</h1>
        <Veld label="Engineer *">
          <select value={f.engineerId} onChange={(e) => setF({ ...f, engineerId: e.target.value })}>
            <option value="">– kies –</option>
            {d.engineers.map((e) => <option key={e.id} value={e.id}>{e.naam}</option>)}
          </select>
        </Veld>
        <Veld label="Soort">
          <select value={f.soort} onChange={(e) => setF({ ...f, soort: e.target.value })}>
            {Object.entries(AFW_SOORT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Veld>
        <div className="grid2">
          <Veld label="Van *"><input type="date" value={f.van} onChange={(e) => setF({ ...f, van: e.target.value, tot: f.tot && f.tot >= e.target.value ? f.tot : e.target.value })} /></Veld>
          <Veld label="Tot en met"><input type="date" value={f.tot} min={f.van} onChange={(e) => setF({ ...f, tot: e.target.value })} /></Veld>
        </div>
        <label className="check"><input type="checkbox" checked={f.heleDag} onChange={(e) => setF({ ...f, heleDag: e.target.checked })} /> Hele dag(en)</label>
        {!f.heleDag && (
          <div className="grid2">
            <Veld label="Begintijd"><select value={f.vanTijd} onChange={(e) => setF({ ...f, vanTijd: e.target.value })}>{TIJDEN.map((t) => <option key={t}>{t}</option>)}</select></Veld>
            <Veld label="Eindtijd" hint={`${urenTussen(f.vanTijd, f.totTijd)} uur per dag`}><select value={f.totTijd} onChange={(e) => setF({ ...f, totTijd: e.target.value })}>{TIJDEN.map((t) => <option key={t}>{t}</option>)}</select></Veld>
          </div>
        )}
        <Veld label="Opmerking"><input value={f.opmerking} onChange={(e) => setF({ ...f, opmerking: e.target.value })} /></Veld>
        <AdresVeld label="Adres elders (optioneel, voor reistijd naar de volgende taak)" waarde={geo} onChange={setGeo} />
        <button className="knop primair" disabled={!f.engineerId || !f.van || opslaan.isPending} onClick={() => opslaan.mutate()}>Opslaan</button>
      </div>
      <div className="paneel">
        <div className="sectie-kop">
          <h2 className="groei">Afwezigheden</h2>
          <select value={filterEng} onChange={(e) => setFilterEng(e.target.value)} style={{ width: 220 }}>
            <option value="">Alle engineers</option>
            {d.engineers.map((e) => <option key={e.id} value={e.id}>{e.naam}</option>)}
          </select>
          <label className="check"><input type="checkbox" checked={toonOud} onChange={(e) => setToonOud(e.target.checked)} /> Ook voorbij</label>
        </div>
        <table className="tabel">
          <thead><tr><th>Engineer</th><th>Soort</th><th>Periode</th><th>Tijd</th><th>Opmerking</th><th /></tr></thead>
          <tbody>
            {lijst.map((a) => (
              <tr key={a.id}>
                <td>{eng(a.engineerId)}</td>
                <td><Badge toon={a.soort === 'ZIEK' ? 'rood' : a.soort === 'VERLOF' ? 'blauw' : 'grijs'}>{AFW_SOORT[a.soort]}</Badge></td>
                <td className="nowrap">{fmtDag(a.van)}{a.tot !== a.van ? ` – ${fmtDag(a.tot)}` : ''}</td>
                <td>{a.vanTijd ? `${a.vanTijd}–${a.totTijd}` : 'hele dag'}</td>
                <td>{a.opmerking}{a.adres ? <div className="klein mute">📍 {a.adres}</div> : null}</td>
                <td className="nowrap">
                  <button className="knop klein" title="Met een dag verlengen" onClick={() => verleng.mutate(a.id)}>+1 dag</button>{' '}
                  <VerwijderKnop klein label="🗑" onBevestig={() => verwijder.mutate(a.id)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!lijst.length && <div className="leeg">Geen afwezigheden.</div>}
      </div>
    </div>
  );
}
